import React from 'react';
import { TrendingUp, Clock, Shield, DollarSign } from 'lucide-react';

interface StatProps {
  value: string;
  label: string;
  icon: React.ReactNode;
  color: string;
}

const Stat: React.FC<StatProps> = ({ value, label, icon, color }) => {
  return (
    <div className="bg-white rounded-xl shadow-md p-6 flex items-start space-x-4">
      <div className={`p-3 rounded-lg ${color} flex-shrink-0`}>
        {icon}
      </div>
      <div>
        <div className="text-2xl md:text-3xl font-bold mb-1">{value}</div>
        <div className="text-gray-500">{label}</div>
      </div>
    </div>
  );
};

const Benefits: React.FC = () => {
  return (
    <section id="benefits" className="py-20">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Measurable Results</h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-lg">
            Our system delivers concrete improvements in efficiency, compliance, and patient safety—with a rapid return on investment.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mb-16">
          <Stat
            value="94%"
            label="Reduction in processing errors"
            icon={<TrendingUp size={24} className="text-white" />}
            color="bg-[#2e609b]"
          />
          <Stat
            value="15+ hours"
            label="Weekly time saved per staff member"
            icon={<Clock size={24} className="text-white" />}
            color="bg-[#1ba63f]"
          />
          <Stat
            value="100%"
            label="Regulatory compliance documentation"
            icon={<Shield size={24} className="text-white" />}
            color="bg-[#2e609b]"
          />
          <Stat
            value="6 months"
            label="Average ROI timeframe"
            icon={<DollarSign size={24} className="text-white" />}
            color="bg-[#1ba63f]"
          />
        </div>

        <div className="bg-gradient-to-r from-[#2e609b] to-[#1ba63f] rounded-2xl overflow-hidden shadow-xl">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            <div className="p-6 md:p-10 lg:p-16">
              <h3 className="text-white text-2xl md:text-3xl font-bold mb-6">Proven Results Across Healthcare Facilities</h3>
              <p className="text-white/90 mb-8 text-lg">
                Our system has been implemented in over 100 healthcare facilities nationwide, from small surgical centers to major hospital networks.
              </p>
              
              <div className="space-y-4">
                <div className="bg-white/10 rounded-lg p-4">
                  <h4 className="text-white font-medium mb-2">Cost Reduction</h4>
                  <p className="text-white/80">
Minimized procedure delays and optimized staff utilization generate significant cost savings for healthcare facilities.                  </p>
                </div>
                
                <div className="bg-white/10 rounded-lg p-4">
                  <h4 className="text-white font-medium mb-2">Staff Satisfaction</h4>
                  <p className="text-white/80">
                    Reduced manual documentation and clearer workflows have improved staff retention by 65% in SPD departments.
                  </p>
                </div>
                
                <div className="bg-white/10 rounded-lg p-4">
                  <h4 className="text-white font-medium mb-2">Sterilization Assurance</h4>
                  <p className="text-white/80">
Automated validation and documentation of sterilization parameters ensures compliance with industry standards.                  </p>
                </div>
              </div>
            </div>
            
            <div className="hidden lg:block relative">
              <img 
                src="https://images.pexels.com/photos/7579831/pexels-photo-7579831.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1" 
                alt="Healthcare team using MediTrack system" 
                className="h-full w-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Benefits;